// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React from 'react';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {
  useRampFlagMetricsSearchAPI,
  useRampFlagWeaveInWorkspace,
  useStatsigGateWorkspaceConsolidation,
} from '../../../util/rampFeatureFlags';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useQuickAddKeys} from '../PanelsQuickAdd/hooks/useQuickAddKeys';
import {useWorkspaceSettingsContext} from '../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {WorkspaceDrawerScroller} from '../WorkspaceDrawerScroller';
import {AddPanelsMenuGroup} from './AddPanelsMenuGroup';
import {
  ADD_PANELS_MENU_GROUPS,
  ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD,
  WEAVE_GROUP,
} from './constants';
import {useDefaultSectionRefs} from './hooks/useDefaultSectionRefs';
import {useIsItemRecommended} from './hooks/useIsItemRecommended';
import {useIsPanelTypeEnabled} from './hooks/useIsPanelTypeEnabled';

export const AddPanels = () => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const {closeWorkspaceDrawer, targetSectionRef: drawerSectionRef} =
    useWorkspaceDrawerContext();
  const {isSingleRun} = useSingleRunModeContext();
  const isPanelTypeEnabled = useIsPanelTypeEnabled();

  const {workspaceLayout} = useWorkspaceSettingsContext();

  // this needs to actually check against false because undefined is auto mode
  const isManualMode = workspaceLayout.shouldAutoGeneratePanels === false;

  const {entityName} = useProjectParams();

  const useMetricsSearchAPI = useRampFlagMetricsSearchAPI(entityName);

  const {keysWithNoPanels} = useQuickAddKeys();
  let menuGroups =
    keysWithNoPanels.length > 0 || (isManualMode && useMetricsSearchAPI)
      ? ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD
      : ADD_PANELS_MENU_GROUPS;

  const isWeaveInWorkspaceEnabled = useRampFlagWeaveInWorkspace();
  if (isWeaveInWorkspaceEnabled) {
    menuGroups = [...menuGroups, WEAVE_GROUP];
  }

  // perf: calling these hooks here instead of for each AddPanelsMenuGroup/AddPanelsMenuItem
  // because they could be expensive for workspaces with lots of sections/panels
  const defaultSectionRefs = useDefaultSectionRefs(menuGroups);
  const isItemRecommended = useIsItemRecommended();

  return (
    <>
      <div className="flex items-center justify-between border-b border-solid border-moon-250 px-24 py-12">
        <h2 className="text-xl font-semibold leading-[40px]">Add panels</h2>
        <Button
          variant="ghost"
          icon="close"
          onClick={() => closeWorkspaceDrawer()}
        />
      </div>
      <WorkspaceDrawerScroller role="menu">
        {menuGroups.map((group, idx) => {
          const filteredItems =
            isSingleRun && !isWorkspaceConsolidationEnabled
              ? group.items.filter(item => !item.isMultiRunOnly)
              : group.items;

          if (!filteredItems.length) {
            return null;
          }

          const defaultSectionRef = group.title
            ? defaultSectionRefs?.get(group.title)
            : undefined;
          const sectionRef = drawerSectionRef ?? defaultSectionRef;
          const itemsWithDisableState = filteredItems.map(item => ({
            ...item,
            isDisabled: !isPanelTypeEnabled(item.type),
            isRecommended: isItemRecommended(item),
          }));

          return (
            <React.Fragment key={idx}>
              {idx !== 0 && (
                <div role="separator" className="h-px bg-moon-250" />
              )}
              <AddPanelsMenuGroup
                {...group}
                sectionRef={sectionRef}
                items={itemsWithDisableState}
              />
            </React.Fragment>
          );
        })}
      </WorkspaceDrawerScroller>
    </>
  );
};
